import React, { memo } from '../../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../../global';

import type { Theme } from '../../../../global/types';
import { ContentTab } from '../../../../global/types';

import { NO_BACKEND, TMAIL_APP_URL } from '../../../../config';
import { selectCurrentAccountSettings } from '../../../../global/selectors';
import { ACCENT_COLORS } from '../../../../util/accentColor/constants';
import buildClassName from '../../../../util/buildClassName';
import buildStyle from '../../../../util/buildStyle';
import { IS_TOUCH_ENV } from '../../../../util/windowEnvironment';
import { openSite } from '../../../explore/helpers/utils';
import { ANIMATED_STICKERS_PATHS } from '../../../ui/helpers/animatedAssets';

import useAppTheme from '../../../../hooks/useAppTheme';
import useFlag from '../../../../hooks/useFlag';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';

import AnimatedIconWithPreview from '../../../ui/AnimatedIconWithPreview';
import Button from '../../../ui/Button';

import styles from './LandscapeNavBar.module.scss';

const ANIMATED_ICON_SIZE_PX = 34;
const ANIMATED_STICKER_SPEED = 2;

interface StateProps {
  areSettingsOpen?: boolean;
  isExploreOpen?: boolean;
  theme: Theme;
  accentColorIndex?: number;
}

function LandscapeNavBar({
  areSettingsOpen, isExploreOpen, theme, accentColorIndex,
}: StateProps) {
  const {
    switchToWallet, switchToExplore, switchToSettings,
    closeNftCollection, selectToken, setActiveContentTab,
  } = getActions();

  const lang = useLang();
  const appTheme = useAppTheme(theme);
  const stickerPaths = ANIMATED_STICKERS_PATHS[appTheme];
  const accentColor = accentColorIndex !== undefined ? ACCENT_COLORS[appTheme][accentColorIndex] : undefined;
  const isWalletActive = !areSettingsOpen && !isExploreOpen;

  const handleWalletClick = useLastCallback(() => {
    switchToWallet();
    closeNftCollection();
    selectToken({ slug: undefined });
    setActiveContentTab({ tab: ContentTab.Assets });
  });

  const handleTmailClick = useLastCallback(() => {
    switchToExplore();
    openSite(TMAIL_APP_URL, undefined, lang('TMail'));
  });

  return (
    <div className={styles.root}>
      <NavButton
        isActive={isWalletActive}
        label={lang('Wallet')}
        tgsUrl={isWalletActive ? stickerPaths.iconWalletSolid : stickerPaths.iconWallet}
        previewUrl={isWalletActive ? stickerPaths.preview.iconWalletSolid : stickerPaths.preview.iconWallet}
        accentColor={accentColor}
        onClick={handleWalletClick}
      />
      {!NO_BACKEND && (
        <NavButton
          isActive={isExploreOpen}
          label={lang('Explore')}
          tgsUrl={isExploreOpen ? stickerPaths.iconExploreSolid : stickerPaths.iconExplore}
          previewUrl={isExploreOpen ? stickerPaths.preview.iconExploreSolid : stickerPaths.preview.iconExplore}
          accentColor={accentColor}
          onClick={switchToExplore}
        />
      )}
      <NavButton
        isActive={areSettingsOpen}
        label={lang('Settings')}
        tgsUrl={areSettingsOpen ? stickerPaths.iconSettingsSolid : stickerPaths.iconSettings}
        previewUrl={areSettingsOpen ? stickerPaths.preview.iconSettingsSolid : stickerPaths.preview.iconSettings}
        accentColor={accentColor}
        onClick={switchToSettings}
      />
      <Button
        isSimple
        className={styles.button}
        onClick={handleTmailClick}
      >
        <span
          className={styles.tmailLogo}
          style={buildStyle(
            `width: ${ANIMATED_ICON_SIZE_PX}px; height: ${ANIMATED_ICON_SIZE_PX}px`,
            accentColor && `--tmail-icon-color: ${accentColor}`,
          )}
        />
        <span className={styles.label}>{lang('TMail')}</span>
      </Button>
    </div>
  );
}

export default memo(withGlobal((global): StateProps => {
  const { areSettingsOpen, isExploreOpen } = global;

  return {
    areSettingsOpen,
    isExploreOpen,
    theme: global.settings.theme,
    accentColorIndex: selectCurrentAccountSettings(global)?.accentColorIndex,
  };
})(LandscapeNavBar));

function NavButtonInternal({
  label, tgsUrl, previewUrl, isActive, accentColor, onClick,
}: {
  isActive?: boolean;
  label: string;
  tgsUrl: string;
  previewUrl: string;
  accentColor?: string;
  onClick: NoneToVoidFunction;
}) {
  const [isAnimating, startAnimation, stopAnimation] = useFlag();

  const handleClick = useLastCallback(() => {
    if (IS_TOUCH_ENV) startAnimation();
    onClick();
  });

  return (
    <Button
      isSimple
      className={buildClassName(styles.button, isActive && styles.active)}
      onClick={handleClick}
      onMouseEnter={!IS_TOUCH_ENV ? startAnimation : undefined}
    >
      <AnimatedIconWithPreview
        play={isAnimating}
        size={ANIMATED_ICON_SIZE_PX}
        speed={ANIMATED_STICKER_SPEED}
        nonInteractive
        forceOnHeavyAnimation
        className={styles.icon}
        color={accentColor}
        tgsUrl={tgsUrl}
        previewUrl={previewUrl}
        onEnded={stopAnimation}
      />
      <span className={styles.label}>{label}</span>
    </Button>
  );
}

const NavButton = memo(NavButtonInternal);
